import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";

interface BoardMainSpaceLayoutProps {
  children?: ReactNode;
  className?: string;
  /**
   * Layout configuration for responsive grid
   */
  layout?: "single" | "two-column" | "three-column" | "custom";
  /**
   * Enable animation transitions between layout changes
   */
  enableAnimations?: boolean;
  /**
   * Gap between grid items
   */
  gap?: "sm" | "md" | "lg" | "none";
}

/**
 * BoardMainSpaceLayout - Provides flexible grid layouts for organizing
 * charts and components in the main board space with animation support
 * and responsive behavior.
 */
export const BoardMainSpaceLayout: React.FC<BoardMainSpaceLayoutProps> = ({
  children,
  className = "",
  layout = "single",
  enableAnimations = true,
  gap = "md",
}) => {
  const { t } = useTranslation("common");
  // Map layout to grid classes
  const getGridClasses = () => {
    const baseClasses = "grid w-full h-full";
    const gapClasses = {
      none: "gap-0",
      sm: "gap-4",
      md: "gap-6",
      lg: "gap-8",
    };
    const layoutClasses = {
      single: "grid-cols-1",
      "two-column": "grid-cols-1 lg:grid-cols-2",
      "three-column": "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
      custom: "grid-cols-1",
    };

    return `${baseClasses} ${layoutClasses[layout]} ${gapClasses[gap]}`;
  };

  // Animation classes
  const animationClasses = enableAnimations
    ? "transition-all duration-300 ease-in-out"
    : "";

  return (
    <div
      className={`
        board-main-space-layout
        ${getGridClasses()}
        ${animationClasses}
        ${className}
      `}
    >
      {children || (
        // Default placeholder content when no children provided
        <div className="col-span-full text-center py-12 text-neutral-500">
          <div className="text-4xl mb-4">🏗️</div>
          <h3 className="text-lg font-medium mb-2">{t("boards.layout.title") || "Board Layout"}</h3>
          <p className="text-sm">
            {t("boards.layout.description") || "Configure your layout with charts and components. Supports responsive grid layouts and smooth animations."}
          </p>
        </div>
      )}
    </div>
  );
};

/**
 * LayoutItem - Individual item within the BoardMainSpaceLayout
 * Provides consistent styling and animation support
 */
interface LayoutItemProps {
  children: ReactNode;
  className?: string;
  span?: 1 | 2 | 3 | "full";
  minHeight?: string;
  enableHover?: boolean;
}

export const LayoutItem: React.FC<LayoutItemProps> = ({
  children,
  className = "",
  span = 1,
  minHeight = "min-h-[200px]",
  enableHover = true,
}) => {
  const spanClasses = {
    1: "col-span-1",
    2: "col-span-1 md:col-span-2",
    3: "col-span-1 md:col-span-3",
    full: "col-span-full",
  };

  return (
    <div
      className={`
        layout-item
        ${spanClasses[span]}
        ${minHeight}
        bg-white
        rounded-lg
        border
        border-neutral-200
        shadow-sm
        p-4
        transition-all
        duration-300
        ease-in-out
        ${enableHover ? "hover:shadow-md hover:border-neutral-300" : ""}
        ${className}
      `}
    >
      {children}
    </div>
  );
};

export default BoardMainSpaceLayout;
